// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Export/Import functionality for vaccine scenarios
 * Supports JSON and CSV formats for stateless sharing
 */

import type { VaccineInputs, VaccineOutputs, Scenario } from '../components/VaccineDashboard/lib/models';
import type { TreeNode, TreeEdge } from './validation';

export interface ExportData {
  version: string;
  timestamp: number;
  scenario?: {
    name: string;
    description?: string;
    audience: string;
    disease: string;
  };
  scenarios?: Scenario[];
  inputs?: VaccineInputs;
  outputs?: VaccineOutputs;
  decisionTree?: {
    nodes: Record<string, TreeNode>;
    edges: TreeEdge[];
    rootId?: string | null;
    costParameters?: any;
    populationParameters?: any;
    modelMetadata?: any;
    simulationParameters?: any;
  };
  activity?: any[],
  hash: string;
}

/**
 * Generate SHA-256 hash for data integrity
 */
async function generateHash(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Export scenario(s) as JSON
 */
export async function exportScenarioJSON(
  scenarios: Scenario | Scenario[],
  decisionTree?: {
    nodes: Record<string, TreeNode>;
    edges: TreeEdge[];
    rootId?: string | null;
    costParameters?: any;
    populationParameters?: any;
    modelMetadata?: any;
    simulationParameters?: any;
  },
  activity?: any[],
): Promise<string> {
  const isArray = Array.isArray(scenarios);
  const primaryScenario = isArray ? (scenarios as Scenario[])[0] : (scenarios as Scenario);
  
  const exportData: Omit<ExportData, 'hash'> = {
    version: '1.1.0',
    timestamp: Date.now(),
  };

  if (isArray) {
    exportData.scenarios = scenarios as Scenario[];
    // For backward compatibility or if we want to show the "active" one at top level?
    // Let's just put them in scenarios array.
    // But if we want to keep some compatibility with previous import, maybe we can populate inputs/outputs from the first one?
    // The previous import expects `inputs` and `scenario` object at top level.
    // Let's keep the structure compatible if it's a single scenario, or if it's multiple, we might need to adjust import logic.
    // Actually, let's just use the new structure.
  } else {
    // Single scenario export (legacy structure compatible)
    exportData.scenario = {
      name: primaryScenario.name,
      audience: primaryScenario.audience,
      disease: primaryScenario.disease,
    };
    exportData.inputs = primaryScenario.inputs;
    exportData.outputs = primaryScenario.outputs;
  }

  if (decisionTree) {
    // willingness_to_pay has no user-facing consumer and is intentionally not
    // persisted (it would otherwise be misleading on re-import). Strip it from
    // the serialized simulationParameters; the store keeps its internal default.
    const { simulationParameters } = decisionTree;
    if (simulationParameters && 'willingness_to_pay' in simulationParameters) {
      const { willingness_to_pay: _wtp, ...simWithoutWtp } = simulationParameters;
      exportData.decisionTree = { ...decisionTree, simulationParameters: simWithoutWtp };
    } else {
      exportData.decisionTree = decisionTree;
    }
  }
  if (activity){
    exportData.activity = activity;
  }

  const dataString = JSON.stringify(exportData, null, 2);
  const hash = await generateHash(dataString);

  const fullExport: ExportData = {
    ...exportData,
    hash,
  };

  return JSON.stringify(fullExport, null, 2);
}

/**
 * Import scenario from JSON
 * @throws Error if hash verification fails
 */
export async function importScenarioJSON(jsonString: string): Promise<ExportData> {
  const data: ExportData = JSON.parse(jsonString);

  // Verify hash
  const { hash, ...dataWithoutHash } = data;
  const dataString = JSON.stringify(dataWithoutHash, null, 2);
  const calculatedHash = await generateHash(dataString);

  if (hash !== calculatedHash) {
    throw new Error(
      'Hash verification failed - data may have been tampered with or corrupted. ' +
      'Please ensure you are importing an unmodified export file.'
    );
  }

  return data;
}

/**
 * Download file to user's computer
 */
export function downloadFile(content: string, filename: string, mimeType: string = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export scenario and trigger download
 */
export async function exportAndDownloadJSON(
  scenarios: Scenario | Scenario[],
  decisionTree?: { 
    nodes: Record<string, TreeNode>; 
    edges: TreeEdge[]; 
    rootId?: string | null;
    costParameters?: any;
    populationParameters?: any;
    modelMetadata?: any;
    simulationParameters?: any;
  },
  activity?: any[],
) {
  const json = await exportScenarioJSON(scenarios, decisionTree, activity);
  
  let filename = 'vaccine-scenario-export.json';
  if (Array.isArray(scenarios)) {
    filename = `vaccine-scenarios-all-${Date.now()}.json`;
  } else {
    filename = `vaccine-scenario-${scenarios.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
  }
  
  downloadFile(json, filename, 'application/json');
}
